<div class="container p-4 bg-whitesmoke">
  <app-breadcrumb></app-breadcrumb>
  
  <div class="d-flex justify-content-end align-items-center mb-4 gap-2">
    <!-- Add and Import Buttons -->
    <button 
      mat-raised-button
      color="primary"
      (click)="openAddEmployeeForm()"
      title="Add New Employee"
      *ngIf="!loading && !error">
      <mat-icon>add</mat-icon>
      Add Employee
    </button>
    <button 
      mat-raised-button
      color="accent"
      (click)="openImportDialog()"
      title="Import from Excel"
      *ngIf="!loading && !error">
      <mat-icon>file_upload</mat-icon>
      Import Excel
    </button>
    <button 
      mat-stroked-button
      color="primary"
      (click)="exportEmployees('excel')"
      title="Export to Excel"
      *ngIf="employees.length > 0 && !loading && !error">
      <mat-icon>file_download</mat-icon>
      Export Excel
    </button>
    <button 
      mat-stroked-button
      (click)="downloadSampleTemplate()"
      title="Download Sample Template"
      *ngIf="!loading && !error">
      <mat-icon>description</mat-icon>
      Sample Template
    </button>

    <!-- View Toggle Buttons -->
    <mat-button-toggle-group 
      [(value)]="currentView" 
      (change)="toggleView($event.value)" 
      class="border"
      *ngIf="!loading && !error && employees.length > 0">
      <mat-button-toggle value="gallery" title="Gallery View">
        <mat-icon>grid_view</mat-icon>
        Gallery
      </mat-button-toggle>
      <mat-button-toggle value="table" title="Table View">
        <mat-icon>view_list</mat-icon>
        Table
      </mat-button-toggle>
    </mat-button-toggle-group>

    <button mat-icon-button (click)="refresh()" [disabled]="loading" title="Refresh" class="border rounded">
      <mat-icon>refresh</mat-icon>
    </button>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="d-flex flex-column align-items-center justify-content-center py-5">
    <mat-spinner diameter="40" class="mb-3"></mat-spinner>
    <p class="text-muted">Loading employees...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="d-flex justify-content-center py-5">
    <mat-card class="w-100 max-w-500">
      <mat-card-content>
        <div class="d-flex align-items-center gap-3">
          <mat-icon color="warn" class="fs-1">warning</mat-icon>
          <div class="flex-grow-1">
            <h5 class="text-danger mb-2">Error Loading Employees</h5>
            <p class="text-muted mb-3">{{ error }}</p>
            <button mat-raised-button color="primary" (click)="refresh()">
              <mat-icon class="me-2">refresh</mat-icon>
              Try Again
            </button>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Empty State -->
  <div *ngIf="!loading && !error && employees.length === 0" class="d-flex justify-content-center py-5">
    <mat-card class="w-100 text-center max-w-500">
      <mat-card-content class="p-5">
        <mat-icon class="text-muted mb-3 mat-icon-4xl">person_outline</mat-icon>
        <h5 class="text-dark mb-3">No Employees Found</h5>
        <p class="text-muted mb-4">There are no employees associated with your account yet.</p>
        <button mat-raised-button color="primary" (click)="openAddEmployeeForm()">
          <mat-icon class="me-2">add</mat-icon>
          Add Your First Employee
        </button>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Employees Grid View -->
  <div *ngIf="!loading && !error && employees.length > 0 && isGalleryView()" class="row g-4">
    <div class="col-12 col-md-6 col-lg-4 col-xl-3" *ngFor="let employee of employees; trackBy: trackByEmployeeId">
      <mat-card class="h-100 shadow-sm border-0 hover-shadow transition-all">
        <mat-card-header class="pb-2">
          <div mat-card-avatar class="d-flex align-items-center justify-content-center bg-light rounded-circle avatar-sm">
            <img 
              *ngIf="employee.profilePicture && employee.profilePicture.trim() !== ''"
              [src]="employee.profilePicture" 
              [alt]="employee.fullName"
              class="rounded-circle w-100 h-100 object-fit-cover"
            />
            <mat-icon 
              *ngIf="!employee.profilePicture || employee.profilePicture.trim() === ''"
              class="text-muted">
              person
            </mat-icon>
          </div>
          <mat-card-title class="h6 text-truncate">{{ employee.fullName || 'Unknown Name' }}</mat-card-title>
          <mat-card-subtitle class="small text-truncate">{{ '@' + (employee.handler || 'No Handler') }}</mat-card-subtitle>
        </mat-card-header>

        <mat-card-content class="py-2">
          <div class="d-flex flex-column gap-2">
            <div class="d-flex align-items-center small" *ngIf="employee.email">
              <mat-icon class="me-2 text-primary mat-icon-sm">email</mat-icon>
              <a [href]="'mailto:' + employee.email" class="text-decoration-none text-truncate">
                {{ employee.email }}
              </a>
            </div>

            <div class="d-flex align-items-center small" *ngIf="employee.phone">
              <mat-icon class="me-2 text-success mat-icon-sm">phone</mat-icon>
              <a [href]="'tel:' + employee.phone" class="text-decoration-none">
                {{ employee.phone }}
              </a>
            </div>

            <div class="d-flex align-items-center small text-muted">
              <mat-icon class="me-2 mat-icon-sm">badge</mat-icon>
              <span class="font-monospace">{{ employee.id }}</span>
            </div>
          </div>
        </mat-card-content>

        <mat-card-actions class="d-flex justify-content-between align-items-center p-3 bg-light">
          <div class="d-flex gap-1">
            <button 
              mat-icon-button 
              color="primary"
              (click)="viewCard(employee)"
              [disabled]="!employee.handler"
              [title]="employee.handler ? 'View ' + employee.fullName + '\'s card' : 'No handler assigned'">
              <mat-icon class="mat-icon-lg">visibility</mat-icon>
            </button>
            <button mat-icon-button color="primary" title="Edit Employee">
              <mat-icon class="mat-icon-lg">edit</mat-icon>
            </button>
            <button 
              mat-icon-button 
              color="warn"
              (click)="deleteEmployee(employee)"
              [disabled]="isDeleting(employee.id)"
              title="Delete Employee">
              <mat-icon *ngIf="!isDeleting(employee.id)" class="mat-icon-lg">delete</mat-icon>
              <mat-spinner *ngIf="isDeleting(employee.id)" diameter="16"></mat-spinner>
            </button>
          </div>
        </mat-card-actions>
      </mat-card>
    </div>
  </div>

  <!-- Employees Table View -->
  <div *ngIf="!loading && !error && employees.length > 0 && isTableView()">
    <mat-card class="border-0 shadow-sm">
      <mat-card-content class="p-0">
        <table mat-table [dataSource]="employees" class="employees-table">
          <!-- Photo Column -->
          <ng-container matColumnDef="photo">
            <th mat-header-cell *matHeaderCellDef>Photo</th>
            <td mat-cell *matCellDef="let employee">
              <div class="table-profile-avatar">
                <img 
                  *ngIf="employee.profilePicture && employee.profilePicture.trim() !== ''"
                  [src]="employee.profilePicture" 
                  [alt]="employee.fullName"
                  class="table-profile-image"
                />
                <mat-icon 
                  *ngIf="!employee.profilePicture || employee.profilePicture.trim() === ''"
                  class="table-profile-placeholder">
                  person
                </mat-icon>
              </div>
            </td>
          </ng-container>

          <!-- Name Column -->
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Name</th>
            <td mat-cell *matCellDef="let employee">
              <div class="employee-name-wrapper">
                <span class="employee-full-name">{{ employee.fullName || 'Unknown Name' }}</span>
              </div>
            </td>
          </ng-container>

          <!-- Handler Column -->
          <ng-container matColumnDef="handler">
            <th mat-header-cell *matHeaderCellDef>Handler</th>
            <td mat-cell *matCellDef="let employee">
              <span class="employee-handler-text">{{ '@' + (employee.handler || 'No Handler') }}</span>
            </td>
          </ng-container>

          <!-- Email Column -->
          <ng-container matColumnDef="email">
            <th mat-header-cell *matHeaderCellDef>Email</th>
            <td mat-cell *matCellDef="let employee">
              <a 
                *ngIf="employee.email" 
                [href]="'mailto:' + employee.email" 
                class="table-email-link"
              >
                {{ employee.email }}
              </a>
              <span *ngIf="!employee.email" class="no-data">-</span>
            </td>
          </ng-container>

          <!-- Phone Column -->
          <ng-container matColumnDef="phone">
            <th mat-header-cell *matHeaderCellDef>Phone</th>
            <td mat-cell *matCellDef="let employee">
              <a 
                *ngIf="employee.phone" 
                [href]="'tel:' + employee.phone" 
                class="table-phone-link"
              >
                {{ employee.phone }}
              </a>
              <span *ngIf="!employee.phone" class="no-data">-</span>
            </td>
          </ng-container>

          <!-- Department Column -->
          <ng-container matColumnDef="department">
            <th mat-header-cell *matHeaderCellDef>Department</th>
            <td mat-cell *matCellDef="let employee">
              <mat-chip *ngIf="employee.department">{{ employee.department }}</mat-chip>
              <span *ngIf="!employee.department" class="no-data">-</span>
            </td>
          </ng-container>

          <!-- Position Column -->
          <ng-container matColumnDef="position">
            <th mat-header-cell *matHeaderCellDef>Position</th>
            <td mat-cell *matCellDef="let employee">
              <span *ngIf="employee.position">{{ employee.position }}</span>
              <span *ngIf="!employee.position" class="no-data">-</span>
            </td>
          </ng-container>

          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let employee">
              <mat-chip 
                [color]="getStatusColor(employee.status || 'active')"
                selected>
                {{ getStatusLabel(employee.status || 'active') }}
              </mat-chip>
            </td>
          </ng-container>

          <!-- ID Column -->
          <ng-container matColumnDef="id">
            <th mat-header-cell *matHeaderCellDef>ID</th>
            <td mat-cell *matCellDef="let employee">
              <span class="employee-id-text">{{ employee.id }}</span>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let employee">
              <div class="table-actions">
                <button 
                  mat-icon-button 
                  color="primary"
                  [title]="employee.handler ? 'View ' + employee.fullName + '\'s card' : 'No handler assigned'"
                  (click)="viewCard(employee)"
                  [disabled]="!employee.handler"
                >
                  <mat-icon>visibility</mat-icon>
                </button>
                <button mat-icon-button color="primary" title="Edit">
                  <mat-icon>edit</mat-icon>
                </button>
                <button 
                  mat-icon-button 
                  color="warn"
                  title="Delete"
                  (click)="deleteEmployee(employee)"
                  [disabled]="isDeleting(employee.id)"
                >
                  <mat-icon *ngIf="!isDeleting(employee.id)">delete</mat-icon>
                  <mat-spinner *ngIf="isDeleting(employee.id)" diameter="20"></mat-spinner>
                </button>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Statistics Footer -->
  <mat-card *ngIf="!loading && !error && employees.length > 0" class="mt-4 border-0 bg-primary text-white">
    <mat-card-content class="p-3">
      <div class="d-flex align-items-center justify-content-center gap-3">
        <mat-icon>group</mat-icon>
        <div class="text-center">
          <div class="h5 mb-1">{{ employees.length }}</div>
          <div class="small">Total Employees</div>
        </div>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Add Employee Modal -->
  <div *ngIf="showAddForm" class="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center z-1050" (click)="closeAddEmployeeForm()">
    <mat-card class="w-100 mx-3 max-w-600 max-h-90vh overflow-y-auto" (click)="$event.stopPropagation()">
      <mat-card-header class="d-flex justify-content-between align-items-center border-bottom">
        <mat-card-title class="mb-0">Add New Employee</mat-card-title>
        <button mat-icon-button (click)="closeAddEmployeeForm()">
          <mat-icon>close</mat-icon>
        </button>
      </mat-card-header>
      
      <mat-card-content class="p-4">
        <form class="needs-validation" (ngSubmit)="saveEmployee()" #employeeForm="ngForm">
          <div class="row g-3">
            <!-- Full Name -->
            <div class="col-12">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Full Name *</mat-label>
                <input 
                  matInput
                  name="fullName"
                  [(ngModel)]="newEmployee.fullName"
                  required
                  placeholder="Enter full name"
                />
                <mat-error>Full name is required</mat-error>
              </mat-form-field>
            </div>

            <!-- Email -->
            <div class="col-12">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Email *</mat-label>
                <input 
                  matInput
                  type="email"
                  name="email"
                  [(ngModel)]="newEmployee.email"
                  required
                  placeholder="Enter email address"
                />
                <mat-error>Email is required</mat-error>
              </mat-form-field>
            </div>

            <!-- Handler -->
            <div class="col-12">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Handler *</mat-label>
                <input 
                  matInput
                  name="handler"
                  [(ngModel)]="newEmployee.handler"
                  required
                  placeholder="Enter handler/username"
                />
                <mat-error>Handler is required</mat-error>
              </mat-form-field>
            </div>

            <!-- Phone -->
            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Phone</mat-label>
              <input 
                matInput
                type="tel"
                name="phone"
                [(ngModel)]="newEmployee.phone"
                placeholder="Enter phone number"
              />
            </mat-form-field>
            </div>

            <!-- Profile Picture URL -->
            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Profile Picture URL</mat-label>
                <input 
                  matInput
                  type="url"
                  name="profilePicture"
                  [(ngModel)]="newEmployee.profilePicture"
                  placeholder="Enter profile picture URL"
                />
              </mat-form-field>
            </div>

            <!-- Department -->
            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Department</mat-label>
                <mat-select name="department" [(ngModel)]="newEmployee.department">
                  <mat-option value="">Select Department</mat-option>
                  <mat-option value="hr">Human Resources</mat-option>
                  <mat-option value="engineering">Engineering</mat-option>
                  <mat-option value="marketing">Marketing</mat-option>
                  <mat-option value="sales">Sales</mat-option>
                  <mat-option value="finance">Finance</mat-option>
                  <mat-option value="operations">Operations</mat-option>
                  <mat-option value="support">Customer Support</mat-option>
                  <mat-option value="admin">Administration</mat-option>
                  <mat-option value="other">Other</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <!-- Position -->
            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Position</mat-label>
                <input 
                  matInput
                  name="position"
                  [(ngModel)]="newEmployee.position"
                  placeholder="Enter job position/title"
                />
              </mat-form-field>
            </div>

            <!-- Status -->
            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Status</mat-label>
                <mat-select name="status" [(ngModel)]="newEmployee.status">
                  <mat-option value="active">Active</mat-option>
                  <mat-option value="inactive">Inactive</mat-option>
                  <mat-option value="on_leave">On Leave</mat-option>
                  <mat-option value="terminated">Terminated</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <!-- Join Date -->
            <div class="col-12 col-md-6">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>Join Date</mat-label>
                <input 
                  matInput
                  [matDatepicker]="picker"
                  name="joinDate"
                  [(ngModel)]="newEmployee.joinDate"
                />
                <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
              </mat-form-field>
            </div>
          </div>
        </form>
      </mat-card-content>

      <mat-card-actions class="form-actions">
        <button mat-button (click)="closeAddEmployeeForm()">
          Cancel
        </button>
        <button 
          mat-raised-button 
          color="primary"
          (click)="saveEmployee()" 
          [disabled]="!employeeForm.valid || saving"
        >
          <mat-icon *ngIf="!saving">save</mat-icon>
          <mat-spinner *ngIf="saving" diameter="20"></mat-spinner>
          {{ saving ? 'Saving...' : 'Save Employee' }}
        </button>
      </mat-card-actions>
    </mat-card>
  </div>

  <!-- Import Excel Modal -->
  <div *ngIf="showImportDialog" class="modal-overlay" (click)="closeImportDialog()">
    <mat-card class="modal-content import-modal" (click)="$event.stopPropagation()">
      <mat-card-header>
        <mat-card-title>Import Employees from Excel</mat-card-title>
        <button mat-icon-button (click)="closeImportDialog()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </mat-card-header>
      
      <mat-card-content class="import-content">
        <div class="file-upload-area" 
             [class.dragover]="isDragOver"
             (dragover)="onDragOver($event)"
             (dragleave)="onDragLeave($event)"
             (drop)="onDrop($event)"
             (click)="triggerFileInput(fileInput)">
          <input
            type="file" 
            #fileInput 
            accept=".xlsx,.xls,.csv"
            class="file-input d-none-important"
          />
          <div class="upload-text">
            <mat-icon class="upload-icon">attach_file</mat-icon>
            <h4>Drop Excel file here or click to browse</h4>
            <p>Supported formats: .xlsx, .xls, .csv</p>
            <button 
              mat-raised-button 
              color="primary"
              type="button" 
              (click)="$event.stopPropagation(); triggerFileInput(fileInput)">
              <mat-icon>folder_open</mat-icon>
              Choose File
            </button>
          </div>
        </div>

        <mat-card *ngIf="selectedFile" class="selected-file">
          <mat-card-content>
            <div class="file-info">
              <mat-icon class="file-icon">description</mat-icon>
              <div class="file-details">
                <span class="file-name">{{ selectedFile.name }}</span>
                <span class="file-size">({{ formatFileSize(selectedFile.size) }})</span>
              </div>
              <button mat-icon-button (click)="removeSelectedFile()" title="Remove file">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </mat-card-content>
        </mat-card>

        <mat-expansion-panel class="excel-format-info">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <mat-icon>info</mat-icon>
              Expected Excel Format
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div class="format-content">
            <mat-table [dataSource]="formatExample" class="format-table">
              <ng-container matColumnDef="fullName">
                <th mat-header-cell *matHeaderCellDef>Full Name*</th>
                <td mat-cell *matCellDef="let element">John Doe</td>
              </ng-container>
              <ng-container matColumnDef="email">
                <th mat-header-cell *matHeaderCellDef>Email*</th>
                <td mat-cell *matCellDef="let element">john&#64;company.com</td>
              </ng-container>
              <ng-container matColumnDef="handler">
                <th mat-header-cell *matHeaderCellDef>Handler*</th>
                <td mat-cell *matCellDef="let element">johndoe</td>
              </ng-container>
              <ng-container matColumnDef="phone">
                <th mat-header-cell *matHeaderCellDef>Phone</th>
                <td mat-cell *matCellDef="let element">+91 98765 43210</td>
              </ng-container>
              <ng-container matColumnDef="department">
                <th mat-header-cell *matHeaderCellDef>Department</th>
                <td mat-cell *matCellDef="let element">engineering</td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="formatColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: formatColumns;"></tr>
            </mat-table>
            <div class="format-notes">
              <h4>Important Notes:</h4>
              <ul>
                <li><strong>Required fields:</strong> Full Name, Email, Handler</li>
                <li><strong>Status options:</strong> active, inactive, on_leave, terminated</li>
                <li><strong>Department options:</strong> hr, engineering, marketing, sales, finance, operations, support, admin, other</li>
                <li><strong>Date format:</strong> YYYY-MM-DD (e.g., 2024-01-15)</li>
                <li><strong>Handler rules:</strong> Only letters, numbers, and underscores allowed</li>
              </ul>
            </div>
          </div>
        </mat-expansion-panel>

        <div class="excel-format-info">
          <h4>📋 Expected Excel Format:</h4>
          <div class="format-table">
            <div class="format-row header">
              <span>Full Name*</span>
              <span>Email*</span>
              <span>Handler*</span>
              <span>Phone</span>
              <span>Department</span>
              <span>Position</span>
              <span>Status</span>
              <span>Join Date</span>
              <span>Profile Picture</span>
            </div>
            <div class="format-row">
              <span>John Doe</span>
              <span>john&#64;company.com</span>
              <span>johndoe</span>
              <span>+91 98765 43210</span>
              <span>engineering</span>
              <span>Software Developer</span>
              <span>active</span>
              <span>2024-01-15</span>
              <span>https://example.com/photo.jpg</span>
            </div>
          </div>
          <div class="format-notes">
            <p><strong>Required fields:</strong> Full Name, Email, Handler</p>
            <p><strong>Status options:</strong> active, inactive, on_leave, terminated</p>
            <p><strong>Department options:</strong> hr, engineering, marketing, sales, finance, operations, support, admin, other</p>
            <p><strong>Date format:</strong> YYYY-MM-DD (e.g., 2024-01-15)</p>
            <p><strong>Handler rules:</strong> Only letters, numbers, and underscores allowed</p>
          </div>
        </div>

        <!-- Import Errors -->
        <mat-card *ngIf="importErrors.length > 0" class="import-errors" color="warn">
          <mat-card-header>
            <mat-card-title>
              <mat-icon color="warn">error</mat-icon>
              Import Errors
            </mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div class="errors-list">
              <div *ngFor="let error of importErrors" class="error-item">
                <mat-icon>error_outline</mat-icon>
                {{ error }}
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </mat-card-content>

      <mat-card-actions class="import-actions">
        <button mat-button (click)="closeImportDialog()">
          Cancel
        </button>
        <button 
          mat-raised-button 
          color="primary"
          [disabled]="!selectedFile || importing"
          (click)="importEmployees()"
        >
          <mat-icon *ngIf="!importing">file_upload</mat-icon>
          <mat-spinner *ngIf="importing" diameter="20"></mat-spinner>
          {{ importing ? 'Importing...' : 'Import Employees' }}
        </button>
      </mat-card-actions>
    </mat-card>
  </div>
</div>
